import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getNextConsentStepUrl, storeConsentApps } from '../auth/adminConsent';
import { ConsentApp, fetchConsentApps } from '../api/consentApps';
import { PageLayout } from '../components/PageLayout';

const isGuid = (value: string | undefined): value is string =>
  !!value && /^[\dA-Fa-f-]{36}$/.test(value);

/**
 * Entry point of the sign-up flow. Reads the invitation id from the URL and
 * shows a landing screen explaining the process. The actual admin-consent
 * redirect only fires after the admin clicks the start button — that way a
 * misclick on the invitation link doesn't immediately bounce them to
 * Microsoft.
 *
 * The ordered consent-app chain is fetched from the backend here (once) and
 * cached in sessionStorage so the consent callback can resume it across the
 * Azure AD redirects.
 */
export const SignUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invitationId } = useParams<{ invitationId: string }>();
  const validInvitation = isGuid(invitationId);
  const [apps, setApps] = useState<ConsentApp[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // True once the admin has pressed the start button. The redirect itself may
  // still be waiting for the consent-app fetch to finish (see effect below).
  const [starting, setStarting] = useState(false);

  // Guards against firing the consent-apps request twice. React StrictMode
  // runs effects twice in development. A `cancelled`-flag cleanup must NOT be
  // combined with this guard: the StrictMode unmount would cancel the only
  // in-flight request while the remount skips re-fetching, so the result would
  // never be applied. The ref alone dedups; the result is always applied.
  const fetchedInvitationId = useRef<string | null>(null);

  useEffect(() => {
    if (!validInvitation || !invitationId) return;
    if (fetchedInvitationId.current === invitationId) return;
    fetchedInvitationId.current = invitationId;

    fetchConsentApps(invitationId)
      .then((result) => {
        storeConsentApps(invitationId, result);
        setApps(result);
      })
      .catch(() => {
        setLoadFailed(true);
      });
  }, [invitationId, validInvitation]);

  const proceed = useCallback(
    (chain: ConsentApp[]) => {
      if (!invitationId) return;
      // An empty chain means the tenant has already consented to every app (a
      // connected-tenant re-entry). There is no consent step left to harvest
      // the tenant id from, so forward to the initialization view, which signs
      // the admin in via MSAL and derives the tenant id from the account.
      // Re-running init is idempotent.
      if (chain.length === 0) {
        navigate(`/initialization?invitationId=${encodeURIComponent(invitationId)}`, {
          replace: true,
        });
        return;
      }
      const next = getNextConsentStepUrl(chain, invitationId, 0);
      if (next) {
        window.location.assign(next);
      }
    },
    [invitationId, navigate],
  );

  // The button is enabled immediately so it never shows a misleading
  // "redirecting" state while the apps load. If the admin clicks before the
  // fetch finishes, redirect as soon as the list arrives.
  useEffect(() => {
    if (starting && apps) {
      proceed(apps);
    }
  }, [starting, apps, proceed]);

  const handleStart = () => {
    if (!validInvitation || !invitationId) return;
    setStarting(true);
    if (apps) {
      proceed(apps);
    }
  };

  if (!validInvitation) {
    return (
      <PageLayout>
        <Typography variant="h5" gutterBottom>
          {t('signup:landing:signUpPage:no_invite_title')}
        </Typography>
        <Typography>{t('signup:landing:signUpPage:no_invite_description')}</Typography>
      </PageLayout>
    );
  }

  if (loadFailed) {
    return (
      <PageLayout>
        <Typography variant="h5" gutterBottom>
          {t('signup:landing:signUpPage:load_failed_title')}
        </Typography>
        <Typography>{t('signup:landing:signUpPage:load_failed_description')}</Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Stack spacing={2.5} alignItems="center">
        <Typography variant="overline" color="text.secondary">
          {t('signup:landing:signUpPage:eyebrow')}
        </Typography>
        <Typography variant="h5">{t('signup:landing:signUpPage:title')}</Typography>
        <Typography color="text.primary">{t('signup:landing:signUpPage:intro')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('signup:landing:signUpPage:steps')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('signup:landing:signUpPage:hint')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleStart}
          disabled={starting}
          sx={{ mt: 1, minWidth: 240 }}
        >
          {starting
            ? t('signup:landing:signUpPage:button_loading')
            : t('signup:landing:signUpPage:button_start')}
        </Button>
      </Stack>
    </PageLayout>
  );
};
